import { SingleDatePicker } from "@/components/SingleDatePicker";
import { TimePicker } from "@/components/TimePicker";
import {
  type TaskModalState,
  type TaskModalStateActions,
} from "@/features/tasks/taskModal/TaskModalReducer";
import { Checkbox, FormField, InlineMessage, Switch } from "@repo/skapa";
import { t } from "@repo/translation";
import { ElementsContainer, TwoColumnElementsContainer } from "@repo/ui";
import { trackAnalytics, validateToDate, validateToTime } from "@repo/utils";
import { isSameDay } from "date-fns";

export const DateForm = ({
  state,
  actions,
  hasCollision,
  isDropInAppointment,
  isDateRangeOnlyTask,
}: {
  state: TaskModalState;
  actions: TaskModalStateActions;
  hasCollision?: boolean;
  isDropInAppointment?: boolean;
  isDateRangeOnlyTask?: boolean;
}) => {
  return (
    <ElementsContainer id="tour-calendar-dates">
      {!isDropInAppointment && !isDateRangeOnlyTask && (
        <Switch
          id="tour-calendar-task-due-date"
          label={t("label.due_date")}
          value="dueDate"
          checked={state.isDueDateTask}
          subtle
          onChange={() => {
            actions.setIsDueTask(!state.isDueDateTask);
            trackAnalytics("set_task_is_due");
          }}
        />
      )}
      {state.isDueDateTask ? (
        <TwoColumnElementsContainer>
          <FormField
            shouldValidate={!state.dueDate}
            valid={Boolean(state.dueDate)}
            validation={{
              msg: t("prompt.invalid_date"),
              id: "error",
              type: "error",
            }}
          >
            <SingleDatePicker
              id="due-date"
              label={t("label.due_date")}
              value={state.dueDate}
              onChange={actions.setDueDate}
              minDate={new Date()}
            />
          </FormField>
          <TimePicker
            id="start-time"
            label={t("label.due_time")}
            value={state.dueTime}
            onChange={actions.setDueTime}
            timeIntervals={5}
            minTime={new Date()}
            required
          />
        </TwoColumnElementsContainer>
      ) : (
        <>
          <TwoColumnElementsContainer>
            <FormField
              shouldValidate={!state.startDate}
              valid={Boolean(state.startDate)}
              validation={{
                msg: t("prompt.invalid_date"),
                id: "error",
                type: "error",
              }}
            >
              <SingleDatePicker
                id="start-date"
                label={t("label.start_date")}
                value={state.startDate}
                onChange={actions.setStartDate}
                minDate={new Date()}
              />
            </FormField>
            <TimePicker
              id="start-time"
              label={t("label.start_time")}
              value={state.startTime}
              onChange={actions.setStartTime}
              timeIntervals={5}
              minTime={new Date()}
              disabled={state.allDay}
              required
            />
          </TwoColumnElementsContainer>

          <TwoColumnElementsContainer>
            <FormField
              shouldValidate={true}
              valid={validateToDate(state.startDate, state.endDate)}
              validation={{
                msg: t("prompt.invalid_date"),
                id: "error",
                type: "error",
              }}
            >
              <SingleDatePicker
                id="end-date"
                label={t("label.end_date")}
                value={state.endDate}
                onChange={actions.setEndDate}
                minDate={state.startDate}
              />
            </FormField>
            <TimePicker
              id="end-time"
              label={t("label.end_time")}
              value={state.endTime}
              onChange={actions.setEndTime}
              timeIntervals={5}
              disabled={state.allDay}
              minTime={
                state.startDate &&
                state.endDate &&
                isSameDay(state.startDate, state.endDate)
                  ? state.startTime
                  : undefined
              }
              isValid={
                state.allDay
                  ? true
                  : validateToTime(
                      state.startDate,
                      state.startTime,
                      state.endDate,
                      state.endTime
                    )
              }
              required
            />
          </TwoColumnElementsContainer>
          {!isDropInAppointment && !isDateRangeOnlyTask && (
            <Switch
              id="tour-calendar-task-all-day"
              label={t("label.all_day")}
              value="allDay"
              checked={state.allDay}
              subtle
              onChange={() => {
                actions.setAllDay(!state.allDay);
                trackAnalytics("set_task_all_day");
              }}
            />
          )}
        </>
      )}
      <ElementsContainer>
        {!state.allDay && !state.isDueDateTask && hasCollision && (
          <InlineMessage
            title={t("prompt.for_your_information")}
            body={t("prompt.double_booked")}
            variant="cautionary"
          />
        )}

        {state.mode === "EDIT" && (
          <Checkbox
            label={t("action.mark_done")}
            value=""
            id="calendar-task-checkbox"
            subtle
            checked={state.isDone}
            onChange={(e) => {
              actions.setIsDone(e.target.checked);
              trackAnalytics("mark_done");
            }}
          />
        )}
      </ElementsContainer>
    </ElementsContainer>
  );
};
